import { Editor, Mark } from '@tiptap/core'
import { Node } from 'prosemirror-model'
import attachmentURLCache from '../helpers/attachmentURLCache'
import { EditorState, Plugin } from 'prosemirror-state'
import { v4 as uuidv4 } from 'uuid'

// Supported image file extensions for inline rendering
const imageExtension = [
  'png',
  'jpeg',
  'jpg',
  'gif',
  'bmp',
  'tiff',
  'ico',
  'svg',
  'webp',
  'drawing',
]

export function uniqueFilename(filename: string, state: EditorState): string {
  if (filename === 'image.png') {
    return uuidv4() + '.png' // Create a uuid for the filename
  }

  let uniqueFilename = filename

  const isFilenameUnique = (filenameToCheck: string) => {
    let foundDuplicate = false
    state.doc.descendants((node: Node) => {
      if (foundDuplicate) {
        return false
      } // Early exit if duplicate is found
      node.marks.forEach((mark) => {
        // Check if the attribute exists
        if (mark.attrs.filename === undefined) {
          return
        }

        const markFilenameLastComponent = mark.attrs.filename.split('/').pop()
        const filenameToCheckLastComponent = filenameToCheck.split('/').pop()
        if (
          mark.type.name === 'inlineAttachment' &&
          markFilenameLastComponent === filenameToCheckLastComponent
        ) {
          foundDuplicate = true
          return
        }
      })
      return true
    })
    return !foundDuplicate
  }

  while (!isFilenameUnique(uniqueFilename)) {
    const extensionIndex = uniqueFilename.lastIndexOf('.')
    const baseName =
      extensionIndex !== -1
        ? uniqueFilename.slice(0, extensionIndex)
        : uniqueFilename
    const extension =
      extensionIndex !== -1 ? uniqueFilename.slice(extensionIndex) : ''
    const randomId = Math.random().toString(36).substring(2, 8) // Generate a short random id
    uniqueFilename = `${baseName}_${randomId}${extension}`
  }

  return uniqueFilename
}

// Utils
export function isFilenameImage(filename: string, title: string): boolean {
  // Check title first
  if (title === 'image') {
    return true
  }

  // Handle web URLs specially
  if (isWebURL(filename)) {
    try {
      // Parse the URL to get the pathname
      const url = new URL(filename)
      const pathname = url.pathname

      // Extract extension from pathname (before query params/fragments)
      const extension = pathname.split('.').pop()?.toLowerCase()
      return imageExtension.includes(extension ?? '')
    } catch {
      // If URL parsing fails, fall back to simple string parsing
      // Remove query parameters and fragments first
      const cleanFilename = filename.split('?')[0].split('#')[0]
      const extension = cleanFilename.split('.').pop()?.toLowerCase()
      return imageExtension.includes(extension ?? '')
    }
  }

  // Handle local files (existing logic)
  const extension = filename.split('.').pop()?.toLowerCase()
  return imageExtension.includes(extension ?? '')
}

export function isWebURL(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false
  }
  return url.startsWith('http://') || url.startsWith('https://')
}

// Gets you the URLs of all attachments in the editor
export function findAttachmentURLs(editor: Editor | null): string[] {
  if (editor == null) {
    return []
  }
  const state = editor.state
  const urls: string[] = []

  state.doc.nodesBetween(0, state.doc.content.size, (node) => {
    node.marks.forEach((mark) => {
      if (mark.type.name === 'inlineAttachment') {
        // Skip web URLs - they don't need to be uploaded or managed as attachments
        const filename = mark.attrs.filename
        if (isWebURL(filename)) {
          return
        }

        // If there's a drawingSrc, first add this. After the drawing always follows the png version of the drawing
        if (mark.attrs.drawingSrc) {
          urls.push(
            JSON.stringify({
              title: mark.attrs.title,
              filename: mark.attrs.filename,
              url: mark.attrs.drawingSrc,
            })
          )
        }

        urls.push(
          JSON.stringify({
            title: mark.attrs.title,
            filename: mark.attrs.drawingSrc
              ? mark.attrs.filename.replace('.drawing', '.png')
              : mark.attrs.filename,
            url: mark.attrs.src,
          })
        )
      }
    })
  })

  return urls
}

// Get the blob url from an image or file
async function downloadAsset(downloadUrl: string): Promise<string> {
  try {
    const response = await fetch(downloadUrl)
    if (!response.ok) {
      throw new Error(`Failed to download asset. Status: ${response.status}`)
    }
    const blob = await response.blob()
    return URL.createObjectURL(blob)
  } catch (error) {
    console.error('Error downloading asset:', error)
    return ''
  }
}

// Updates the attachment with the new URL and marks it as downloaded so the image gets rendered and it doesn't get re-downloaded
function setAttachmentAttribute(
  editor: Editor,
  url: string,
  downloadUrl: string | null,
  drawingUrl: string | null,
  HTMLAttributes: any
) {
  const { state, view } = editor

  // Prevent crashes when editor is not ready
  if (!(view as any).docView) return

  const tr = state.tr
  var nodeToUpdate: { pos: number; node: Node; mark: any } | null = null

  state.doc.nodesBetween(0, state.doc.content.size, (node, pos) => {
    node.marks.forEach((mark) => {
      if (mark.type.name === 'inlineAttachment') {
        if (downloadUrl && mark.attrs.downloadUrl === downloadUrl) {
          nodeToUpdate = { pos, node, mark }
        } else if (drawingUrl && mark.attrs.drawingUrl === drawingUrl) {
          nodeToUpdate = { pos, node, mark }
        }
        return
      }
    })
  })

  if (nodeToUpdate) {
    const { pos, node, mark } = nodeToUpdate as {
      pos: number
      node: Node
      mark: any
    }

    var attr = HTMLAttributes

    if (downloadUrl) {
      attr.downloaded = true
      attr.src = url
    } else if (drawingUrl) {
      attr.drawingSrc = url
    }

    tr.addMark(pos, pos + node.nodeSize, mark.type.create(attr))
  }

  const newState = state.apply(tr)
  view.updateState(newState)
}

// Actual inline attachment extension

export const InlineAttachment = Mark.create({
  name: 'inlineAttachment',
  content: 'inline*',
  selectable: true,
  indentable: false,

  addAttributes() {
    return {
      title: {
        default: false,
        parseHTML: (element) => element.getAttribute('title'),
        renderHTML: (attributes) => ({
          title: attributes.title,
        }),
      },
      downloaded: {
        default: false,
        parseHTML: (element) => element.getAttribute('downloaded'),
        renderHTML: (attributes) => ({
          downloaded: attributes.downloaded,
        }),
      },
      filename: {
        default: null,
        parseHTML: (element) => element.getAttribute('filename'),
        renderHTML: (attributes) => ({
          filename: attributes.filename,
        }),
      },
      downloadUrl: {
        default: null,
        parseHTML: (element) => element.getAttribute('downloadUrl'),
        renderHTML: (attributes) => ({
          downloadUrl: attributes.downloadUrl,
        }),
      },
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute('src'),
        renderHTML: (attributes) => ({
          src: attributes.src,
        }),
      },
      drawingUrl: {
        default: null,
        parseHTML: (element) => element.getAttribute('drawingUrl'),
        renderHTML: (attributes) => ({
          drawingUrl: attributes.drawingUrl,
        }),
      },
      drawingSrc: {
        default: null,
        parseHTML: (element) => element.getAttribute('drawingSrc'),
        renderHTML: (attributes) => ({
          drawingSrc: attributes.drawingSrc,
        }),
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('width'),
        renderHTML: (attributes) => ({
          width: attributes.width,
        }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'img',
        getAttrs: (element: HTMLElement | string) => {
          if (typeof element === 'string') {
            return false
          }

          if (element.getAttribute('data-content-type') === this.name) {
            return {
              src: element.getAttribute('src') || null,
            }
          }

          return false
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const {
      src,
      filename,
      downloadUrl,
      downloaded,
      title,
      drawingUrl,
      drawingSrc,
      width,
    } = HTMLAttributes
    const isImage = isFilenameImage(filename ?? '', title ?? '')

    // When the user downloads the file, give it a proper name
    let downloadName = filename
      ? decodeURIComponent(filename.split('/').pop())
      : 'download'

    // If we have a proper title, use that as the downloadName
    if (title.length > 0 && title !== 'image' && title !== 'file') {
      downloadName = title
    }

    let assetSrc = HTMLAttributes.src

    // Check if this is a web URL - if so, use it directly
    if (isWebURL(filename)) {
      assetSrc = filename
    } else if (!src && filename && downloadUrl && !downloaded) {
      if (attachmentURLCache.has(filename) && !drawingUrl) {
        setAttachmentAttribute(
          this.editor!,
          attachmentURLCache.get(filename)!,
          downloadUrl,
          null,
          HTMLAttributes
        )

        // Update it here otherwise it won't be set
        assetSrc = attachmentURLCache.get(filename)!
      } else if (downloadUrl && downloadUrl.length > 0) {
        // If it's available, we need to download the drawing and the png. We use two assets, but show only one. The png is for the web version to display the drawing.
        if (drawingUrl && !drawingSrc) {
          downloadAsset(drawingUrl).then((url) => {
            if (!url || url.length === 0) {
              return
            }
            setAttachmentAttribute(
              this.editor!,
              url,
              null,
              drawingUrl,
              HTMLAttributes
            )
          })
        }

        // If the downloadUrl is set, download the asset
        downloadAsset(downloadUrl).then((url) => {
          if (!url || url.length === 0) {
            return
          }

          // Cache the url to the blob so we don't need to redownload the asset
          attachmentURLCache.set(filename, url)
          setAttachmentAttribute(
            this.editor!,
            url,
            downloadUrl,
            null,
            HTMLAttributes
          )
        })

        // Temporarily load the old one again if cached
        assetSrc = attachmentURLCache.get(filename) || assetSrc
      }
    }

    if (!isImage) {
      // Determine if we should show loading spinner for files
      // Only show spinner when there's actually a downloadUrl to fetch
      // Files use the same download logic as images above, so assetSrc should be set if downloaded
      const showLoadingSpinner = !isWebURL(String(filename)) && !assetSrc && !downloaded && !!downloadUrl
      
      return [
        'span',
        {
          ...HTMLAttributes,
          'data-content-type': this.name,
          contenteditable: 'false',
        },
        ...(showLoadingSpinner
          ? [['div', { class: 'loading-spinner-sm' }]]
          : []),
        [
          'a',
          {
            href: assetSrc || downloadUrl || '#',
            target: '_self',
            download: downloadName,
          },
          ['i', { class: 'far fa-paperclip' }],
          ['span', 0],
        ],
      ]
    } else {
      // For web URLs, don't show loading spinner since image loads directly
      const showLoadingSpinner = !isWebURL(filename) && !assetSrc

      // Calculate the image width style
      let imageStyle = 'height: auto;'
      let containerStyle = ''
      if (width) {
        if (width.endsWith('%')) {
          // Percentage width - use max-width for the container
          // This ensures the container properly wraps the image
          imageStyle = `width: 100%; height: auto;`
          containerStyle = `width: ${width}; max-width: 100%;`
        } else {
          // Pixel width - absolute value with max-width constraint
          imageStyle = `width: 100%; height: auto;`
          containerStyle = `width: ${width}px; max-width: 100%;`
        }
      } else {
        // Default width if not specified
        imageStyle = `width: 100%; height: auto;`
        containerStyle = `max-width: 100%;`
      }

      return [
        'span',
        {
          isDrawing: drawingUrl ? 'true' : 'false',
          ...HTMLAttributes,
          'data-content-type': this.name,
          style: containerStyle,
          contenteditable: 'false',
        },
        // Only show loading spinner for local attachments that haven't loaded yet
        ...(showLoadingSpinner
          ? [
              [
                'div',
                { class: 'loading-spinner-sm', style: 'position: absolute;' },
              ],
            ]
          : []),
        [
          'img',
          {
            src: assetSrc,
            style: imageStyle, // Apply calculated width style
            // Add error handling for web images
            onerror: isWebURL(filename)
              ? 'this.style.display="none"; this.nextSibling.style.display="inline";'
              : undefined,
          },
        ],
        // Add fallback text for failed web images
        ...(isWebURL(filename)
          ? [
              [
                'span',
                { style: 'display: none; color: #999; font-style: italic;' },
                'Failed to load image',
              ],
            ]
          : []),
        [
          'a',
          {
            href: src || assetSrc,
            download: downloadName,
            target: '_self',
            style: 'padding: 5px;', // Keep padding, positioning handled by CSS
            class: 'download-button', // Add a class for styling
          },
          [
            'i',
            {
              class: 'fa fa-circle-down',
              style:
                'color: #000000AA; font-size: 19px; padding: 4px; background: rgba(255, 255, 255, 0.9); border-radius: 50%;',
            },
          ],
          ['div', 0], // Use FontAwesome icon
        ],
      ]
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction: (transactions, _oldState, newState) => {
          // Only process if there are actual changes
          if (!transactions.some((tr) => tr.docChanged)) {
            return
          }

          // Skip if this transaction was created by our own plugin to prevent recursion
          const isOurTransaction = transactions.some((tr) =>
            tr.getMeta('inlineAttachmentProcessed')
          )
          if (isOurTransaction) {
            return
          }

          const tr = newState.tr
          let modified = false

          // Find markdown image syntax in the document text (with optional size parameter)
          // Supports both angle-bracketed <url> and plain url (for filenames with parentheses/spaces)
          const fullText = newState.doc.textContent
          const regex = /!\[([^[\]]*?)(?:\|(\d+%?))?]\(((?:<[^>]+>)|[^()]+)\)/g
          let match: RegExpExecArray | null

          // eslint-disable-next-line unicorn/no-null -- regex exec returns null
          while ((match = regex.exec(fullText)) != null) {
            const title = match[1] || 'image'
            const size = match[2] || undefined // Optional size parameter
            // Strip angle brackets from CommonMark-style destinations: <url> -> url
            const rawFilename = match[3]
            const filename = rawFilename.startsWith('<') && rawFilename.endsWith('>') 
              ? rawFilename.slice(1, -1).trim() 
              : rawFilename

            // Handle multi-node matches by finding the start and end positions
            let found = false
            let accumulatedText = ''
            let startPos = -1
            let endPos = -1
            const textNodes: {
              pos: number
              text: string
              length: number
            }[] = []

            // First pass: collect all text nodes and build accumulated text
            newState.doc.descendants((node, pos) => {
              if (node.type.name === 'text' && node.text) {
                textNodes.push({
                  pos,
                  text: node.text,
                  length: node.text.length,
                })
                accumulatedText += node.text
              }
              return true
            })

            // Check if our match is in the accumulated text
            if (accumulatedText.includes(match[0])) {
              const matchStartInAccumulated = accumulatedText.indexOf(match[0])
              const matchEndInAccumulated =
                matchStartInAccumulated + match[0].length

              // Find which text nodes contain the start and end of our match
              let currentTextPos = 0
              let foundStart = false
              let foundEnd = false

              for (const textNode of textNodes) {
                const nodeStart = currentTextPos
                const nodeEnd = currentTextPos + textNode.length

                // Check if match starts in this node
                if (
                  !foundStart &&
                  matchStartInAccumulated >= nodeStart &&
                  matchStartInAccumulated < nodeEnd
                ) {
                  startPos =
                    textNode.pos + (matchStartInAccumulated - nodeStart)
                  foundStart = true
                }

                // Check if match ends in this node
                if (
                  !foundEnd &&
                  matchEndInAccumulated > nodeStart &&
                  matchEndInAccumulated <= nodeEnd
                ) {
                  endPos = textNode.pos + (matchEndInAccumulated - nodeStart)
                  foundEnd = true
                }

                if (foundStart && foundEnd) {
                  found = true
                  break
                }

                currentTextPos = nodeEnd
              }
            }

            if (!found) {
              continue
            }

            const matchStart = startPos
            const matchEnd = endPos

            // Check if this range already has an inlineAttachment mark
            try {
              const existingMarks = newState.doc.rangeHasMark(
                matchStart,
                matchEnd,
                this.type
              )
              if (existingMarks) {
                continue // Skip if already marked
              }
            } catch {
              continue
            }

            // Create attributes based on URL type
            const attributes = isWebURL(filename)
              ? {
                  title: title,
                  filename: filename,
                  downloadUrl: filename,
                  downloaded: true,
                  src: filename,
                  width: size ?? undefined,
                }
              : {
                  title: title,
                  filename: filename,
                  downloadUrl: undefined,
                  downloaded: false,
                  src: undefined,
                  width: size ?? undefined,
                }

            // Replace the markdown syntax with the title text and apply the mark
            try {
              tr.insertText(title, matchStart, matchEnd)
              tr.addMark(
                matchStart,
                matchStart + title.length,
                this.type.create(attributes)
              )
              // Mark this transaction as processed by our plugin to prevent recursion
              tr.setMeta('inlineAttachmentProcessed', true)
              modified = true
            } catch {
              // Silently continue if transformation fails
            }
          }

          return modified ? tr : undefined
        },
      }),
    ]
  },

  // addProseMirrorPlugins() {
  //   const plugins = [
  //     new Plugin({
  //       props: {
  //         // This `props` object can contain ProseMirror view props, such as event handlers
  //         handleDOMEvents: {
  //           mouseover(view, event) {
  //             console.log("mouse over", event.target);
  //             const downloadButton = (
  //               event.target as HTMLElement
  //             ).querySelector(".download-button") as HTMLElement;
  //             if (downloadButton) {
  //               downloadButton.style.display = "block"; // Show the download button
  //               return true;
  //             }
  //             return false; // Return true if the event is handled and should not propagate
  //           },
  //           mouseout(view, event) {
  //             console.log("mouse out", event.target);
  //             const downloadButton = (
  //               event.target as HTMLElement
  //             ).querySelector(".download-button") as HTMLElement;
  //             if (downloadButton) {
  //               downloadButton.style.display = "none"; // Hide the download button
  //               return true; // Event is handled
  //             }

  //             return false;
  //           },
  //         },
  //       },
  //     }),
  //   ];

  //   return plugins;
  // },
})
